import type { Content } from '@/modules/Entertainment/interface/Content';
import {
  type QueryKey,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { api } from '@/libs/axios/apiClient';
import { apiConfig } from '@/config/api';
import { authKeys } from '@/config/keys';
import { getCookie } from '@/utils/cookies';
import { checkIsTokenValid } from '@/utils/jwt';

const changeBookmark = async ({
  id,
  object,
  bookmarked,
}: Pick<Content, 'id' | 'object' | 'bookmarked'>) => {
  try {
    if (!bookmarked) {
      await api.post(apiConfig.routes.bookmark.save, {
        type: object,
        content_id: id,
      });
      return;
    }

    await api.delete(apiConfig.routes.bookmark.delete(id, object));
  } catch (error) {
    const authToken = getCookie(authKeys.authToken);

    if (!checkIsTokenValid(authToken)) {
      window.dispatchEvent(new Event('open-unauthenticated-modal'));
    }

    throw new Error('Error on bookmark');
  }
};

export const useBookmark = (queryKeyToInvalidate?: QueryKey) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: changeBookmark,
    onSettled: () =>
      queryKeyToInvalidate &&
      queryClient.invalidateQueries({ queryKey: queryKeyToInvalidate }),
  });
};
